import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { Grfq } from '#/unified-rfqs/entities/grfq/domain';
import { Drfq } from '#/unified-rfqs/entities/drfq/domain';
import { Rfq, RfqId } from '#/unified-rfqs/entities/rfq';

interface NotificationHandlerActions {
  readonly pauseNotificationsFor: (pauseMillis: number) => void;
  readonly pauseNotifications: () => void;
  readonly resumeNotifications: () => void;
  readonly increaseNewRfqCount: () => void;
  readonly resetNewRfqCount: () => void;
  readonly resetStoredNotifications: () => void;
  /**
   * Register user trade in a given rfq. We do this lock the rfq in the screen.
   * Preventing it from moving to the top. (PTP-9515, PTP-9578)
   */
  readonly storeUserTrade: (rfqId: RfqId) => void;
  /**
   * If notification is paused, skip and store the ADD notification to be executed later.
   * We skip execution so the rfqs from the table does not move and disrupt
   * the user action.
   */
  handleRfqReceived<T extends Drfq | Grfq>(
    rfq: T,
    handler: (rfq: Partial<T>) => void,
  ): void;
  /**
   * Receives an update notification for the RFQ.
   * It removes the latest_activity to prevent the RFQ from being moved in the table, it happens if:
   * - The notifications are paused
   * - The user has traded recently on this RFQ
   */
  handleRfqUpdated<T extends Drfq | Grfq>(
    rfq: T,
    handler: (rfq: Partial<T>) => void,
  ): void;
}

interface NotificationHandlerView {
  readonly newRfqCount: number;
  readonly isNotificationPaused: boolean;
}

export const [useNotificationHandlerActions, ActionsProvider] =
  createSafeContext<NotificationHandlerActions>('NotificationHandlerActions');

export const [useNotificationHandlerView, ViewProvider] =
  createSafeContext<NotificationHandlerView>('NotificationHandlerView');

// After receiving a trade, rfq will not move with new updates
const LOCK_USER_TRADED_RFQ_MS = 15_000;

export default function NotificationHandlerProvider({
  children,
}: PropsWithChildren) {
  const [isNotificationPaused, setIsNotificationPaused] = useState(false);
  const [newRfqCount, setNewRfqCount] = useState(0);

  const storedNotificationsRef = useRef<Set<() => void>>(new Set([]));
  const resumeTimeoutIdRef = useRef<ReturnType<typeof setTimeout>>();
  const isNotificationPausedRef = useRef<boolean>();
  const userLastTradesRef = useRef<Map<RfqId, number>>(new Map());

  const actions: NotificationHandlerActions = useMemo(
    () => ({
      increaseNewRfqCount: () => setNewRfqCount((count) => count + 1),
      resetNewRfqCount: () => setNewRfqCount(0),
      resetStoredNotifications: () => {
        storedNotificationsRef.current = new Set([]);
      },
      storeUserTrade: (rfqId: RfqId) => {
        userLastTradesRef.current.set(rfqId, Date.now());
      },
      handleRfqUpdated<T extends Drfq | Grfq>(
        rfq: T,
        handler: (rfq: Partial<T>) => void,
      ) {
        const rfqLastTradedAt = userLastTradesRef.current.get(rfq.id);
        const isLockedByRecentTrade =
          rfqLastTradedAt != null &&
          Date.now() - rfqLastTradedAt <= LOCK_USER_TRADED_RFQ_MS;

        // PTP-9515, PTP-9578: If user just submitted an one-click trade on the RFQ,
        // remove latest_activity so it does not go to the top.
        const shouldRemoveLatestActivity =
          isNotificationPausedRef.current === true || isLockedByRecentTrade;

        if (shouldRemoveLatestActivity) {
          handler(removeLatestActivity(rfq) as unknown as Partial<T>);
        } else {
          handler(rfq);
        }
      },
      handleRfqReceived: (rfq, handler) => {
        if (isNotificationPausedRef.current === false) {
          console.info(`ADDED RFQ ${rfq.id} from WS to store:`, rfq);
          handler(rfq);
          return;
        }
        console.info(
          `prevent ADDED RFQ ${rfq.id} from WS to store because notification is paused:`,
          rfq,
        );

        storedNotificationsRef.current.add(() => handler(rfq));
        actions.increaseNewRfqCount();
      },
      pauseNotifications: () => setIsNotificationPaused(true),
      pauseNotificationsFor: (pauseMs) => {
        setIsNotificationPaused(true);
        clearTimeout(resumeTimeoutIdRef.current);

        resumeTimeoutIdRef.current = setTimeout(() => {
          actions.resumeNotifications();
        }, pauseMs);
      },
      resumeNotifications: () => {
        if (isNotificationPausedRef.current === false) return;
        setIsNotificationPaused(false);
        for (const handler of storedNotificationsRef.current) {
          handler();
        }

        actions.resetNewRfqCount();
        actions.resetStoredNotifications();
      },
    }),
    [],
  );

  const view: NotificationHandlerView = useMemo(
    () => ({
      isNotificationPaused,
      newRfqCount,
    }),
    [isNotificationPaused, newRfqCount],
  );

  useEffect(() => {
    isNotificationPausedRef.current = isNotificationPaused;
  }, [isNotificationPaused]);

  return (
    <ViewProvider value={view}>
      <ActionsProvider value={actions}>{children}</ActionsProvider>
    </ViewProvider>
  );
}

function removeLatestActivity(rfq: Rfq) {
  if (rfq.type === 'grfq') {
    const { latest_activity, ...restRfq } = rfq;

    return restRfq;
  }

  const { last_updated_at, ...restRfq } = rfq;
  return restRfq;
}
